import { Profile } from "@redux/modules/profile";
import {
    CMSCventProps,
    CMSEventProps,
    CMSPersonProps,
    CombinedCvent,
    EventError,
    EventRegistrationMethod,
    EventSession,
    EventStatus,
    EventTrack,
    EventType,
    LiveEventRegistrationTimeframe,
    QuickEvent,
} from "@types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { TFunction } from "i18next";
import yn from "yn";

import quickEventHelper from "./../quick-event-helper";

dayjs.extend(localizedFormat);
dayjs.extend(isBetween);

//get the status of the session
const getSessionStatus = (session: EventSession): EventStatus => {
    if (dayjs(session?.end_date_time).isBefore(dayjs())) return EventStatus.LIVE_FINALISED;
    if (dayjs(session?.start_date_time).isBefore(dayjs())) return EventStatus.LIVE_NOW;
    if (dayjs(session?.start_date_time).isAfter(dayjs().add(30, "minutes"))) return EventStatus.UPCOMING_LIVE;
    if (dayjs(session?.start_date_time).isBetween(dayjs(), dayjs().add(30, "minutes"))) return EventStatus.LIVE_SOON;
};

//calculate the status of an entire track
const getTrackStatus = (track: EventTrack): EventStatus => {
    const statuses = track?.sessions?.map((session) => getSessionStatus(session.session));
    if (statuses?.includes(EventStatus.LIVE_NOW)) return EventStatus.LIVE_NOW;
    if (statuses?.includes(EventStatus.LIVE_SOON)) return EventStatus.LIVE_SOON;
    if (statuses?.includes(EventStatus.UPCOMING_LIVE)) return EventStatus.UPCOMING_LIVE;
    return EventStatus.LIVE_FINALISED;
};

//calculate the status of an entire event
const getEventStatus = (event: CMSEventProps | CMSCventProps | QuickEvent | CombinedCvent): EventStatus => {
    if (event.internal?.type === "Contentstack_event") {
        if (event?.type === EventType.ON_DEMAND) return EventStatus.ON_DEMAND;
        const statuses = event?.event_structure?.map((track) => getTrackStatus(track.track));
        if (statuses?.includes(EventStatus.LIVE_NOW)) return EventStatus.LIVE_NOW;
        if (statuses?.includes(EventStatus.LIVE_SOON)) return EventStatus.LIVE_SOON;
        if (statuses?.includes(EventStatus.UPCOMING_LIVE)) return EventStatus.UPCOMING_LIVE;
        return EventStatus.LIVE_FINALISED;
    } else if (event.internal?.type === "Contentstack_cvent") {
        const startTime = dayjs(event.startDate);
        const endTime = dayjs(event.endDate);
        const currentTime = dayjs();

        if (endTime.isBefore(currentTime)) {
            return EventStatus.LIVE_FINALISED;
        }
        if (currentTime.isBetween(startTime, endTime, "second", "[]")) {
            return EventStatus.LIVE_NOW;
        }
        if (startTime.isAfter(currentTime.add(30, "minutes"))) {
            return EventStatus.UPCOMING_LIVE;
        }
        return EventStatus.LIVE_SOON;
    } else {
        const startTime = dayjs(event.startTime);
        const endTime = dayjs(event.endTime);
        const currentTime = dayjs();

        if (endTime.isBefore(currentTime)) {
            return EventStatus.LIVE_FINALISED;
        }
        if (currentTime.isBetween(startTime, endTime, "second", "[]")) {
            return EventStatus.LIVE_NOW;
        }
        if (startTime.isAfter(currentTime.add(30, "minutes"))) {
            return EventStatus.UPCOMING_LIVE;
        }
        return EventStatus.LIVE_SOON;
    }
};

//hook that check if a user can see an event or not
export const useCanEventBeDisplayed = (event: CMSEventProps, isEligible: boolean, profile: Profile): EventError => {
    // 1. Initial eligibility and data checks
    if (!isEligible) {
        // eslint-disable-next-line no-console
        console.info("not showing event because user is inelligible", event?.iconnect_event_number);
        return EventError.INELIGIBLE;
    }
    //data is absent
    if (!event || !profile) {
        // eslint-disable-next-line no-console
        console.info("not showing event because data missing", event?.iconnect_event_number);
        return EventError.DATA_MISSING;
    }

    // 2. Check if the event is "too old" (unpublished) based on different criteria
    const isOutsideDate = isExpiredOnDemandEvent(event, profile);

    //user is eligible but event is no longer valid for publish limit
    if (isOutsideDate) {
        // eslint-disable-next-line no-console
        console.info("not showing event because event is too old", event?.iconnect_event_number);
        return EventError.UNPUBLISHED;
    }

    // 3. Check for closed on-demand events where the user is not registered/attended
    if (isClosedOnDemandEventAndUserIsNotEligible(event)) {
        // eslint-disable-next-line no-console
        console.info("Not showing event because you're too late", event?.iconnect_event_number);
        return EventError.UNATTENDED;
    }

    // 4. If all checks pass, the event can be displayed
    return EventError.ALL_CLEAR;
};

/**
 * The start date of the event is the starting moment of the first session
 * The end date of the event is the ending moment of the last session
 */
const getStartAndEndDate = (event: CMSEventProps): { startDateTime: string; endDateTime: string } => {
    let startDateTime = event?.event_structure?.[0]?.track.sessions[0]?.session.start_date_time || null;
    let endDateTime = event?.event_structure?.[0]?.track.sessions[0]?.session.end_date_time || null;

    event?.event_structure?.forEach(({ track }) => {
        track.sessions.forEach(({ session }) => {
            if (dayjs(session.start_date_time).isBefore(dayjs(startDateTime))) {
                startDateTime = session.start_date_time;
            }
            if (dayjs(session.end_date_time).isAfter(dayjs(endDateTime))) {
                endDateTime = session.end_date_time;
            }
        });
    });

    return { startDateTime, endDateTime };
};

/**
 * For Live events:
 * - Upcoming Live: There is no ongoing session and the next session starts in more than 30 minutes
 * - Live Soon:  There is no ongoing session and the next session starts within 30 minutes
 * - Live now: There is an ongoing session
 * - Live finalised: All the sessions have passed
 */

const getTrackingStatus = (event: CMSEventProps | CMSCventProps | QuickEvent): string => {
    const EVENTSTATUS_MAP = new Map<string, string>([
        [EventStatus.LIVE_NOW, "livenow"],
        [EventStatus.UPCOMING_LIVE, "livesoon"],
        [EventStatus.LIVE_SOON, "livesoon"],
        [EventStatus.LIVE_FINALISED, "livefinalised"],
        [EventStatus.ON_DEMAND, null],
    ]);

    return EVENTSTATUS_MAP.get(getEventStatus(event));
};

const getSpeakers = (event: CMSEventProps): CMSPersonProps[] => {
    return event?.event_structure?.reduce((acc, current) => {
        current.track.sessions.forEach((session) => {
            session?.session?.speakers?.forEach((speaker) => {
                if (!acc.find((s) => s.id === speaker.id)) {
                    acc.push(speaker);
                }
            });
        });
        return acc;
    }, [] as CMSPersonProps[]);
};

const getTimeString = (start: string, end: string, t: TFunction): string => {
    const twentyFourHourNotation = yn(process.env.GATSBY_24_HOUR_NOTATION);

    if (!start || !end) {
        return null;
    }

    if (start.length > 10 || end.length > 10)
        return `${t("common:From")} ${dayjs(start).format(twentyFourHourNotation ? "HH:mm" : "LT")} ${t(
            "common:till",
        )} ${dayjs(end).format(twentyFourHourNotation ? "HH:mm" : "LT")}`;
};

const getDurationString = (start: string, end: string): string => {
    if (!start || !end) {
        return null;
    }
    const hours = dayjs(end).diff(dayjs(start), "hour");

    if (hours < 1) {
        const min = dayjs(end).diff(dayjs(start), "minute");
        if (min <= 0) {
            return null;
        }
        return `${min}min`;
    } else {
        return `${hours}h`;
    }
};

const getLiveStatusString = (status: EventStatus, t: TFunction, shortened?: boolean): string => {
    if (shortened && status === EventStatus.LIVE_NOW) {
        return t("Live", { ns: "events" });
    }
    const STATUS_MAP = new Map<EventStatus, string>([
        [EventStatus.UPCOMING_LIVE, t("Live soon", { ns: "events" })],
        [EventStatus.LIVE_SOON, t("Live soon", { ns: "events" })],
        [EventStatus.LIVE_NOW, t("Live now", { ns: "events" })],
        [EventStatus.LIVE_FINALISED, t("Live finalised", { ns: "events" })],
        [EventStatus.ON_DEMAND, t("On demand", { ns: "events" })],
    ]);
    return STATUS_MAP.get(status);
};

const sortEvents = (
    events: (CMSEventProps | QuickEvent | CMSCventProps)[],
): (CMSEventProps | QuickEvent | CMSCventProps)[] => {
    return events.sort(
        (a: CMSEventProps | QuickEvent | CMSCventProps, b: CMSEventProps | QuickEvent | CMSCventProps) => {
            const getStatus = (event: CMSEventProps | QuickEvent | CMSCventProps): EventStatus => {
                // Check if event is a quickevent
                if (event?.name) return quickEventHelper.getStatus(event as QuickEvent);
                return getEventStatus(event as CMSEventProps);
            };
            const statusA = getStatus(a);
            const statusB = getStatus(b);

            if (statusA == EventStatus.LIVE_FINALISED && statusB == EventStatus.LIVE_FINALISED) {
                const startDateA =
                    "agendas" in a
                        ? (a as QuickEvent).startTime
                        : a.internal?.type === "Contentstack_cvent"
                          ? dayjs((a as CMSCventProps).startDate)
                          : getStartAndEndDate(a as CMSEventProps).startDateTime;
                const startDateB =
                    "agendas" in b
                        ? (b as QuickEvent).startTime
                        : b.internal?.type === "Contentstack_cvent"
                          ? dayjs((b as CMSCventProps).startDate)
                          : getStartAndEndDate(b as CMSEventProps).startDateTime;
                return dayjs(startDateA).isBefore(startDateB) ? 1 : dayjs(startDateA).isAfter(startDateB) ? -1 : 0;
            }

            if (statusA == EventStatus.ON_DEMAND && statusB == EventStatus.ON_DEMAND) {
                const dateA =
                    "onDemandStartDate" in a
                        ? (a as CMSEventProps).onDemandStartDate
                        : (a as QuickEvent)?.unpublishDate;
                const dateB =
                    "onDemandStartDate" in b
                        ? (b as CMSEventProps).onDemandStartDate
                        : (b as QuickEvent)?.unpublishDate;

                // Fallback to onDemandEndDate or unpublishDate for descending order
                return dayjs(dateA).isBefore(dateB) ? 1 : dayjs(dateA).isAfter(dateB) ? -1 : 0;
            }

            if (statusA === statusB) {
                const startDateA =
                    "agendas" in a
                        ? (a as QuickEvent).startTime
                        : a.internal?.type === "Contentstack_cvent"
                          ? dayjs((a as CMSCventProps).startDate)
                          : getStartAndEndDate(a as CMSEventProps).startDateTime;
                const startDateB =
                    "agendas" in b
                        ? (b as QuickEvent).startTime
                        : b.internal?.type === "Contentstack_cvent"
                          ? dayjs((b as CMSCventProps).startDate)
                          : getStartAndEndDate(b as CMSEventProps).startDateTime;

                return dayjs(startDateA).isBefore(startDateB) ? -1 : dayjs(startDateA).isAfter(startDateB) ? 1 : 0;
            }

            if (
                statusA === EventStatus.LIVE_NOW &&
                [
                    EventStatus.UPCOMING_LIVE,
                    EventStatus.LIVE_SOON,
                    EventStatus.LIVE_FINALISED,
                    EventStatus.ON_DEMAND,
                ].includes(statusB)
            ) {
                return -1;
            }
            if (statusA === EventStatus.LIVE_SOON) {
                if ([EventStatus.UPCOMING_LIVE, EventStatus.LIVE_FINALISED, EventStatus.ON_DEMAND].includes(statusB)) {
                    return -1;
                } else {
                    return 1;
                }
            }

            if (statusA === EventStatus.UPCOMING_LIVE) {
                if ([EventStatus.LIVE_FINALISED, EventStatus.ON_DEMAND].includes(statusB)) {
                    return -1;
                } else {
                    return 1;
                }
            }
            if (statusA === EventStatus.ON_DEMAND) {
                if ([EventStatus.LIVE_FINALISED].includes(statusB)) {
                    return -1;
                } else {
                    return 1;
                }
            }
            if (statusA === EventStatus.LIVE_FINALISED) {
                return 1;
            }

            return 0;
        },
    );
};

const getRegistrationMethod = (event: CMSEventProps): EventRegistrationMethod => {
    if (event.type === EventType.LIVE) {
        return event.live_event_information?.registration_method;
    }
    return event.on_demand_event_information?.registration_method;
};

const isRegistrationMethodManual = (event: CMSEventProps): boolean => {
    return getRegistrationMethod(event) === EventRegistrationMethod.MANUAL;
};

/**
 * For live events, the registration period is either open till the start of the event or till the end of the event.
 * For on demand events, the registration period is open until configured otherwise.
 */
const isRegistrationPeriodOpen = (event: CMSEventProps): boolean => {
    if (event.type === EventType.LIVE) {
        const status = getEventStatus(event);
        if (
            event.live_event_information?.registration_timeframe === LiveEventRegistrationTimeframe.UNTIL_START_OF_EVENT
        ) {
            return status === EventStatus.UPCOMING_LIVE || status === EventStatus.LIVE_SOON;
        } else {
            return status !== EventStatus.LIVE_FINALISED;
        }
    } else if (event.type === EventType.ON_DEMAND) {
        return !event.on_demand_event_information?.closed_registration;
    }
};

const userCanRegister = (event: CMSEventProps): boolean => {
    return isRegistrationMethodManual(event) && !event.registered && isRegistrationPeriodOpen(event);
};

/**
 * For manual registration: the user has to be registered manually.
 * For automatic registration: the registration period has to be open.
 */
const isUserRegistered = (event: CMSEventProps): boolean => {
    return (
        (isRegistrationMethodManual(event) && event.registered) ||
        (!isRegistrationMethodManual(event) && isRegistrationPeriodOpen(event)) ||
        (!isRegistrationMethodManual(event) && !isRegistrationPeriodOpen(event) && (event.attended || event.registered))
    );
};

const canUserAttend = (event: CMSEventProps): boolean => {
    const status = getEventStatus(event);

    if (status !== EventStatus.LIVE_FINALISED) {
        return isUserRegistered(event);
    }
    return false;
};

const canDownloadDocument = (event: CMSEventProps): boolean => {
    const status = getEventStatus(event);
    const registrationMethodManual = isRegistrationMethodManual(event);

    if (registrationMethodManual && event.registered) {
        return true;
    }

    if (!registrationMethodManual && event.attended) {
        return true;
    }

    if (
        !registrationMethodManual &&
        ["LIVE_SOON", "LIVE_NOW", "UPCOMING_LIVE", "ON_DEMAND"].includes(status) &&
        !event.attended
    ) {
        return true;
    }

    return false;
};

const canAddToCalendar = (event: CMSEventProps): boolean => {
    const status = getEventStatus(event);
    const registrationMethodManual = isRegistrationMethodManual(event);

    if (registrationMethodManual && event.registered && ["LIVE_SOON", "LIVE_NOW", "UPCOMING_LIVE"].includes(status)) {
        return true;
    }

    if (!registrationMethodManual && ["LIVE_SOON", "LIVE_NOW", "UPCOMING_LIVE"].includes(status)) {
        return true;
    }

    return false;
};

const getTrackStartEndTime = (track: EventTrack): { startDateTime: string; endDateTime: string } => {
    const status = getTrackStatus(track);
    // If track is UPCOMING_LIVE, we should not take start times of sessions that are in the past, see bug: AFWN-11541
    const sessions =
        status === EventStatus.UPCOMING_LIVE
            ? track?.sessions.filter(({ session }) => dayjs(session.start_date_time).isAfter(dayjs()))
            : track?.sessions;

    let startDateTime = sessions?.[0]?.session.start_date_time || null;
    let endDateTime = sessions?.[0]?.session.end_date_time || null;

    sessions?.forEach(({ session }) => {
        if (dayjs(session.start_date_time).isBefore(dayjs(startDateTime))) {
            startDateTime = session.start_date_time;
        }
        if (dayjs(session.end_date_time).isAfter(dayjs(endDateTime))) {
            endDateTime = session.end_date_time;
        }
    });
    return { startDateTime, endDateTime };
};

const hasRecordingsInTrack = (track: EventTrack): boolean => {
    return track.sessions.some(
        ({ session }: { session: EventSession }) => session.recording?.length > 0 && !!session.recording[0],
    );
};

const hasLiveStreamInTrack = (track: EventTrack): boolean => {
    return track.livestream?.livestream_id?.length > 0;
};

const getTimerString = (track: EventTrack): string => {
    const { startDateTime } = getTrackStartEndTime(track);
    const time = dayjs(startDateTime).diff(dayjs(), "minute", true);
    if (time < 0 || isNaN(time)) {
        return null;
    }
    return `${Math.floor(time / 60 / 24)}d:${Math.floor((time / 60) % 24)}h:${(time % 60).toFixed(0)}m`;
};

const isClosedOnDemandEventAndUserIsNotEligible = (event: CMSEventProps): boolean => {
    return (
        event?.type === EventType.ON_DEMAND &&
        event?.on_demand_event_information?.closed_registration &&
        !(event?.registered || event?.attended)
    );
};

//TODO when all future events have onDemandStartDate, remove function
const isOnDemandEventOutsidePublishLimit = (event: CMSEventProps, publishLimit?: number): boolean => {
    return (
        event.type === EventType.ON_DEMAND &&
        (publishLimit || publishLimit === 0) &&
        dayjs(event.publish_details.time).isBefore(dayjs().subtract(publishLimit, "day"))
    );
};

const isOnDemandEventStarted = (event: CMSEventProps, onDemandStartDate?: string): boolean =>
    event.type === EventType.ON_DEMAND && !dayjs(onDemandStartDate).isAfter(dayjs(), "day");

export const isExpiredOnDemandEvent = (event: CMSEventProps, profile: Profile): boolean => {
    //new OnDemand events have an onDemandStartDate
    if ("onDemandStartDate" in event && event.onDemandStartDate) {
        return !isOnDemandEventStarted(event, event.onDemandStartDate);
    } else {
        //TODO when all future events have onDemandStartDate, remove this else
        const publishLimits =
            process.env.GATSBY_EVENT_PUBLISH_LIMIT && JSON.parse(process.env.GATSBY_EVENT_PUBLISH_LIMIT);
        const publishLimit =
            publishLimits &&
            publishLimits[
                Object.keys(publishLimits).find((key) => key.toLowerCase() === profile?.country?.toLowerCase())
            ];
        //old OnDemand events not filled onDemandStartDate
        return isOnDemandEventOutsidePublishLimit(event, publishLimit);
    }
};

const isPublishedQuickEvent = (event: QuickEvent): boolean => {
    if (!event) return false;
    if (!event?.publishDate || !event?.unpublishDate) return false;
    const now = dayjs();
    const publishDate = dayjs(event.publishDate);
    const unpublishDate = dayjs(event.unpublishDate);
    return now.isAfter(publishDate) && now.isBefore(unpublishDate);
};

const getHeightSize = (height: string): string => {
    switch (height) {
        case "auto":
            return "auto";
        case "Extra Small":
            return "15vh";
        case "Small":
            return "30vh";
        case "Medium":
            return "45vh";
        case "Large":
            return "70vh";
        default:
            return "auto";
    }
};

export default {
    getStartAndEndDate,
    getEventStatus,
    getTrackingStatus,
    getSpeakers,
    getTimeString,
    getDurationString,
    getLiveStatusString,
    sortEvents,
    isUserRegistered,
    canUserAttend,
    userCanRegister,
    isRegistrationMethodManual,
    getTimerString,
    getTrackStartEndTime,
    getRegistrationMethod,
    getHeightSize,
    hasRecordingsInTrack,
    hasLiveStreamInTrack,
    isClosedOnDemandEventAndUserIsNotEligible,
    isRegistrationPeriodOpen,
    isOnDemandEventOutsidePublishLimit,
    isExpiredOnDemandEvent,
    isOnDemandEventStarted,
    getTrackStatus,
    getSessionStatus,
    isPublishedQuickEvent,
    canDownloadDocument,
    canAddToCalendar,
};
